import type { BridgeState } from "./types";
import { parseBridgeState } from "./state";

export const STORAGE_KEY = "ql.dreamBridge.v1";
export const CONSENT_KEY = "ql.dreamBridge.consent";

/**
 * Check whether the visitor explicitly gave consent for saving Bridge progress locally.
 */
export function isConsentGiven(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    return window.localStorage.getItem(CONSENT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Set visitor consent.
 * If consent is revoked (false), saved bridge data is automatically purged.
 */
export function setConsent(consent: boolean): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (consent) {
      window.localStorage.setItem(CONSENT_KEY, "1");
    } else {
      window.localStorage.removeItem(CONSENT_KEY);
      clearSaved();
    }
  } catch {
    // Ignore quota or security restrictions
  }
}

/**
 * Check if saved data exists in storage.
 */
export function hasSavedData(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Load saved bridge state from localStorage.
 * Returns null if consent has not been given, storage is blocked, or content is invalid.
 * NOTE: Sensitive reflection answers are NEVER stored or read here.
 */
export function loadSaved(): BridgeState | null {
  try {
    if (!isConsentGiven()) return null;
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parseBridgeState(parsed);
  } catch {
    return null;
  }
}

/**
 * Save bridge progress to localStorage.
 * No-op if consent has not been granted.
 * Protected against quota limits and blocked storage.
 */
export function saveProgress(state: BridgeState): void {
  try {
    if (!isConsentGiven()) return;
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore
  }
}

/**
 * Clear saved bridge state from localStorage.
 */
export function clearSaved(): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}
